import { useState, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Mui Components
import { Divider } from "@mui/material";

// Components
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import CustomButton from "examples/NewDesign/CustomButton";
import CustomDrawer from "components/Drawer/CustomDrawer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import pxToRem from "assets/theme/functions/pxToRem";
import ExplicitSearchBar from "components/Search/ExplicitSearchBar";
import FilterDropdown from "components/Dropdown/FilterDropdown";
import ResetFilterButton from "components/Buttons/ResetButton";

// Data File
import ProductSummaryData from "layouts/resources/data/productSummaryData";
import productrSummaryDataFormat from "utils/Types/InventorySummary";

// Redux
import { ProductSummaryListThunk } from "redux/Thunks/Equipment";
import { equipmentConfig } from "redux/Thunks/Config";

// Utils
import Constants, {
  Icons,
  Colors,
  defaultData,
  PageTitles,
  FiltersModuleName,
  Common,
} from "utils/Constants";

// Other
import { useDispatch, useSelector } from "react-redux";
import { paramCreater } from "utils/methods/methods";

import InventoryRegister from "./InventoryRegisterForm/InventoryRegister";
// Constants
const INITIAL_FILTERS = [
  {
    inputLabel: FiltersModuleName.CATEGORY,
    list: [FiltersModuleName.COMMON_FILTERS_TITLE_OBJ],
    selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
    isLoading: false,
    isSearchable: true,
  },
  {
    inputLabel: FiltersModuleName.TYPE,
    list: [FiltersModuleName.COMMON_FILTERS_TITLE_OBJ],
    selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
    isLoading: false,
    isSearchable: true,
  },
  {
    inputLabel: FiltersModuleName.PURCHASE_TYPE,
    list: FiltersModuleName.PURCHASE_TYPE_FILTER_OPTIONS,
    selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
    isSearchable: false,
  },
  {
    inputLabel: FiltersModuleName.QUANTITY_TYPE,
    list: FiltersModuleName.QUANTITY_TYPE_FILTER_OPTIONS,
    selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
    isSearchable: false,
  },
];

function ProductSummary() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [inventorySummary, setInventorySummary] = useState({
    loading: Constants.PENDING,
    data: [],
  });
  const [paginationThing, setPaginationThing] = useState({
    totalCount: 0,
    completed: [],
  });
  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.PER_PAGE_6,
  });
  const [sort, setSort] = useState({
    equipmentType: Common.ASC_KEY,
    equipmentCategory: Common.ASC_KEY,
  });
  const [openInventoryModal, setOpenInventoryModal] = useState({ right: false });
  const ConfigData = useSelector((state) => state.config);
  const permission = ConfigData?.screens?.[8]?.screensInfo?.agreement;
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [shouldUpdateState, setShouldUpdateState] = useState(false);
  const isFirstMount = useRef(true);

  // Reset all states to initial values
  const resetAllStates = useCallback(() => {
    // Create a fresh copy of INITIAL_FILTERS to avoid reference issues
    const freshInitialFilters = INITIAL_FILTERS.map((filter) => ({
      ...filter,
      list: Array.isArray(filter.list) ? [...filter.list] : filter.list,
      selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
    }));

    setFilters(freshInitialFilters);
    setInventorySummary({
      loading: Constants.FULFILLED,
      data: [],
    });
    setTablePagination({
      page: 0,
      perPage: defaultData.PER_PAGE_6,
    });
    setPaginationThing({
      totalCount: 0,
      completed: [],
    });
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      setDebounceTimeout(null);
    }
  }, [debounceTimeout]);

  // Param Func
  const createParamObj = (filter, pagination) => {
    const paramData = {
      ...pagination,
      equipmentCategory: filter[0].selectedValue,
      equipmentType: filter[1].selectedValue,
      priceType: filter[2].selectedValue,
      quantityType: filter[3].selectedValue,
    };

    Object.keys(paramData).forEach((key) => {
      if (
        paramData[key] === "" ||
        paramData[key] === FiltersModuleName.ALL_IN_SMALL_CASE ||
        paramData[key] === "All" ||
        paramData[key] === null ||
        paramData[key] === undefined
      ) {
        delete paramData[key];
      }
    });

    return paramData;
  };

  const createProductSummaryListFunc = (resObj = {}, meta = {}) => {
    const { totalCount = 0, InventorySummary = [] } = resObj;

    // Get pagination info from the meta argument
    const params = new URLSearchParams(meta.arg.param);
    const page = parseInt(params.get("page"), 10);
    const perPage = parseInt(params.get("perPage"), 10);

    // If inventorySummary.data is empty, initialize it with the required format
    const inventorySummaryList =
      page === 0
        ? Array.from({ length: totalCount }, () => ({ ...productrSummaryDataFormat }))
        : [...inventorySummary.data];

    // Calculate start index for pagination and replace elements in the array
    const startIndex = page * perPage;
    inventorySummaryList.splice(startIndex, perPage, ...InventorySummary);

    const visitedPagesArr = page === 0 ? [page] : [...paginationThing.completed, page];

    setInventorySummary((prev) => ({
      ...prev,
      data: inventorySummaryList,
    }));
    setPaginationThing({ totalCount, completed: visitedPagesArr });
  };

  // Func to get Inventory Summary list
  const getInventorySummaryData = async (filterValue = filters, currentSort = sort) => {
    setInventorySummary({
      loading: Constants.PENDING,
      data: [],
    });
    try {
      const pagination = { page: defaultData.PAGE, perPage: tablePagination.perPage };
      const params = createParamObj(filterValue, pagination);
      const data = {
        body: {
          sort: {
            equipmentCategory: currentSort.equipmentCategory === Common.ASC_KEY ? 1 : -1,
            equipmentType: currentSort.equipmentType === Common.ASC_KEY ? 1 : -1,
          },
        },
        param: paramCreater(params),
      };
      const res = await dispatch(ProductSummaryListThunk(data));
      if (res.error) {
        throw new Error(res.error.message);
      }
      createProductSummaryListFunc(res.payload.data.data, res.meta);
      setInventorySummary((prev) => ({
        ...prev,
        loading: Constants.FULFILLED,
      }));
    } catch (error) {
      console.error("Error fetching inventory summary:", error);
      setInventorySummary({
        loading: Constants.FULFILLED,
        data: [],
      });
    }
  };

  // Initialize component
  useEffect(() => {
    resetAllStates();
    getInventorySummaryData();
    dispatch(equipmentConfig());
    return () => {
      resetAllStates();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    getInventorySummaryData();
  }, [shouldUpdateState]);

  // Handle search for Category and Type filters
  const handleSearch = useCallback(
    (searchValue, filterIndex) => {
      if (!inventorySummary?.data) return;

      // Clear any existing timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
        setDebounceTimeout(null);
      }

      const updatedFilters = [...filters];
      updatedFilters[filterIndex].isLoading = true;
      setFilters(updatedFilters);

      try {
        let suggestions = [];
        const filterLabel = filters[filterIndex].inputLabel;

        // Get all available options regardless of current filters
        const allData = inventorySummary?.data || [];
        switch (filterLabel) {
          case FiltersModuleName.CATEGORY:
            suggestions = allData.map((item) => ({
              [Constants.MONGOOSE_ID]:
                item.equipmentCategory || FiltersModuleName.ALL_IN_SMALL_CASE,
              title: item.equipmentCategory || "All",
            }));
            break;
          case FiltersModuleName.TYPE:
            suggestions = allData.map((item) => ({
              [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
              title: item.equipmentType,
            }));
            break;
          default:
            suggestions = [];
        }

        // Remove duplicates from all suggestions
        const uniqueSuggestions = suggestions?.reduce((acc, current) => {
          const duplicate = acc.find((item) => item.title === current.title);
          if (!duplicate && current.title) {
            acc.push(current);
          }
          return acc;
        }, []);

        // Store complete list of options with "All" at the beginning
        const allOptions = [
          { [Constants.MONGOOSE_ID]: FiltersModuleName.ALL_IN_SMALL_CASE, title: "All" },
          ...uniqueSuggestions,
        ];

        // Update filters with new state
        const newFilters = [...filters];
        newFilters[filterIndex].list = allOptions;
        newFilters[filterIndex].isLoading = false;

        // Handle the search value
        if (
          !searchValue ||
          searchValue.trim() === "" ||
          searchValue.toLowerCase() === FiltersModuleName.ALL_IN_SMALL_CASE
        ) {
          newFilters[filterIndex].selectedValue = FiltersModuleName.ALL_IN_SMALL_CASE;
        } else {
          newFilters[filterIndex].selectedValue = searchValue.toLowerCase().trim();
        }

        setFilters(newFilters);
        setTablePagination((prev) => ({ ...prev, page: 0 }));
        setPaginationThing((prev) => ({ ...prev, completed: [] }));
        getInventorySummaryData(newFilters);
      } catch (error) {
        console.error("Error in search:", error);
        const errorFilters = [...filters];
        errorFilters[filterIndex].isLoading = false;
        errorFilters[filterIndex].selectedValue = FiltersModuleName.ALL_IN_SMALL_CASE;
        setFilters(errorFilters);
        setInventorySummary((prev) => ({
          ...prev,
          loading: Constants.FULFILLED,
        }));
      }
    },
    [filters, inventorySummary?.data, debounceTimeout]
  );

  // Handle explicit search trigger
  const handleExplicitSearch = useCallback(
    (inputValue, filterIndex) => {
      // Trigger loader immediately
      setInventorySummary((prev) => ({ ...prev, loading: Constants.PENDING }));
      // If inputValue is provided (from Enter key or search button), use it.
      // Otherwise, use the selected value from filters if available.
      handleSearch(inputValue, filterIndex, false);
    },
    [handleSearch]
  );

  // Handle dropdown selection
  const handleDropdownSelection = useCallback(
    (value, filterIndex) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      const searchValue =
        value === null || value === undefined ? FiltersModuleName.ALL_IN_SMALL_CASE : value;
      handleSearch(searchValue, filterIndex, true);
    },
    [handleSearch, debounceTimeout]
  );

  // Handle filter change for non-searchable filters
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = filters.map((filter) =>
      filter.inputLabel === name ? { ...filter, selectedValue: value } : filter
    );
    setFilters(updatedFilters);
    setTablePagination((prev) => ({ ...prev, page: 0 }));
    setPaginationThing((prev) => ({ ...prev, completed: [] }));
    getInventorySummaryData(updatedFilters);
  };

  // Handle reset filters
  const handleReset = useCallback(() => {
    // Create a fresh copy of INITIAL_FILTERS
    const resetFilters = INITIAL_FILTERS.map((filter) => ({
      ...filter,
      list: Array.isArray(filter.list) ? [...filter.list] : filter.list,
      selectedValue: FiltersModuleName.ALL_IN_SMALL_CASE,
      isLoading: false,
    }));

    setFilters(resetFilters);
    setSort({
      equipmentType: Common.ASC_KEY,
      equipmentCategory: Common.ASC_KEY,
    });
    setTablePagination((prev) => ({ ...prev, page: 0 }));
    setPaginationThing((prev) => ({ ...prev, completed: [] }));
    getInventorySummaryData(resetFilters);
  }, []);

  // Handle reload
  const handleReload = () => {
    setInventorySummary({
      loading: Constants.PENDING,
    });
    setTablePagination((prev) => ({ ...prev, page: 0 }));
    setPaginationThing((prev) => ({ ...prev, completed: [] }));
    getInventorySummaryData();
  };

  // Handle pagination
  const handleTablePagination = async (value) => {
    if (paginationThing.completed.includes(value)) return;

    try {
      setInventorySummary((prev) => ({
        ...prev,
        loading: Constants.PENDING,
      }));
      const pagination = { page: value, perPage: tablePagination.perPage };
      const params = createParamObj(filters, pagination);
      const data = {
        body: {
          sort: {
            equipmentCategory: sort.equipmentCategory === Common.ASC_KEY ? 1 : -1,
            equipmentType: sort.equipmentType === Common.ASC_KEY ? 1 : -1,
          },
        },
        param: paramCreater(params),
      };
      const res = await dispatch(ProductSummaryListThunk(data));
      if (res.error) {
        throw new Error(res.error.message);
      }
      createProductSummaryListFunc(res.payload.data.data, res.meta);
      setInventorySummary((prev) => ({
        ...prev,
        loading: Constants.FULFILLED,
      }));
    } catch (error) {
      console.error("Error fetching next page of inventory summary:", error);
      setInventorySummary({
        loading: Constants.FULFILLED,
        data: [],
      });
    }
  };

  // Open Inventory drawer
  const openInventoryRegisterModalFunc = (open) => {
    setOpenInventoryModal({ right: open });
  };

  const handleInventoryDrawerClose = () => {
    setOpenInventoryModal({ right: false });
  };

  // Handle sorting
  const handleSort = (field) => {
    setTablePagination((prev) => ({ ...prev, page: 0 }));
    setPaginationThing((prev) => ({ ...prev, completed: [] }));

    setSort((prev) => {
      const newSort = { ...prev };

      if (newSort[field] === Common.ASC_KEY) {
        newSort[field] = Common.DESC_KEY;
      } else {
        newSort[field] = Common.ASC_KEY;
      }

      // Fetch with updated sort
      getInventorySummaryData(filters, newSort);
      return newSort;
    });
  };

  // Sort handlers
  const handleCategorySort = () => handleSort("equipmentCategory");
  const handleTypeSort = () => handleSort("equipmentType");

  const { columns, rows } = ProductSummaryData(
    inventorySummary.data,
    sort,
    handleCategorySort,
    handleTypeSort
  );
  return (
    <DashboardLayout module={defaultData.EQUIPMENT_SCREEN_ID}>
      <MDBox key={location.pathname}>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <PageTitle title={PageTitles?.EQUIPMENT_SUMMARY} />
          <MDBox display="flex" flexDirection="row">
            {permission?.create && (
              <CustomButton
                title="Register"
                icon="add_circle_outline"
                background={Colors.PRIMARY}
                color={Colors.WHITE}
                openModal={openInventoryRegisterModalFunc}
              />
            )}
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: "var(--gray-300, #D0D5DD)",
                height: "auto",
                marginLeft: pxToRem(16),
                marginRight: 0,
              }}
            />

            <BasicButton
              icon={Icons.RELOAD}
              background={Colors.WHITE}
              border
              color={Colors.BLACK}
              action={handleReload}
            />
          </MDBox>
        </MDBox>

        <Divider sx={{ marginTop: 2 }} />

        {/* Filter and Search Section */}
        <MDBox display="flex" justifyContent="space-between">
          <MDBox>
            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "start",
                alignItems: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              {/* Searchable Filters (Category and Type) */}
              {filters
                .filter((filter) => filter.isSearchable)
                .map((filter, index) => (
                  <ExplicitSearchBar
                    key={filter.inputLabel}
                    freeSolos
                    width={pxToRem(200)}
                    options={filter.list.map((item) => item.title)}
                    label={filter.inputLabel}
                    value={
                      filter?.selectedValue &&
                      filter.selectedValue !== FiltersModuleName.ALL_IN_SMALL_CASE
                        ? filter.selectedValue.charAt(0).toUpperCase() +
                          filter.selectedValue.substring(1).toLowerCase()
                        : FiltersModuleName.ALL_WITH_FIRST_LETTER_CAPITAL
                    }
                    placeholder={`Search ${filter.inputLabel}`}
                    isLoading={filter.isLoading}
                    allowEmptySearch
                    onSearchTrigger={(value) => handleExplicitSearch(value, index)}
                    handleFilterChange={(e, value) => handleDropdownSelection(value, index)}
                  />
                ))}

              {/* Normal Dropdown Filters (Purchase Type and Quantity Type) */}
              {filters
                .filter((filter) => !filter.isSearchable)
                .map((filter) => (
                  <FilterDropdown
                    key={filter.inputLabel}
                    label={filter.inputLabel}
                    name={filter.inputLabel}
                    value={filter.selectedValue}
                    handleChange={handleFilterChange}
                    menu={filter.list}
                  />
                ))}

              {/* Reset Button */}
              <ResetFilterButton handleReset={handleReset} style={{ marginLeft: "1rem" }} />
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Product Summary Data Table */}
        <MDBox mt={2}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE_6 }}
            showTotalEntries={false}
            noEndBorder
            loading={inventorySummary.loading}
            pagination={{ variant: "gradient", color: "info" }}
            currentPage={tablePagination.page}
            handleTablePagination={handleTablePagination}
            handleCurrentPage={(page) => setTablePagination({ ...tablePagination, page })}
            isGotoVisisble
          />
        </MDBox>
      </MDBox>

      {/* Drawer to Register Inventory Equipment */}
      <CustomDrawer
        defaultAnchor={openInventoryModal}
        width="80%"
        onDrawerClose={handleInventoryDrawerClose}
      >
        <InventoryRegister
          closeDrawerFunc={handleInventoryDrawerClose}
          setShouldUpdateState={setShouldUpdateState}
        />
      </CustomDrawer>
    </DashboardLayout>
  );
}

export default ProductSummary;
